import React, { useEffect, useState, useContext } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { HeaderInner } from '../components/HeaderInner';
import { Carousel } from '../components/Carousel';
import { SearchSidebar } from '../components/Search/SearchSidebar';
import { StickySearch } from '../components/Search/StickySearch';
import { LocationInner } from '../components/LocationPage/LocationInner';
import { MobileOverlay } from '../components/Modules/MobileOverlay';
import { SearchContext } from '../context/SearchContext/SearchContext';
import { twoWeeks, twoWeeksTomorrow } from '../utils/defaultDates';
import { Footer } from '../components/Footer';
import CMSService from '../services/CMSApi/CMSService';
import LandingPage from '../models/api/cms/LandingPage';
import Head from '../components/Head/Head';
import LandingPagesCarouselSlide from '../models/api/cms/LandingPagesCarouselSlide';
import { UrlContext } from '../context/UrlContext/UrlContext';
import { SiteParams } from '../config/constants/SiteParams';

// Format a URL slug into a human-readable name
const slugToName = (slug: string): string => {
  return slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

export const LocationPage: React.FC = () => {
  const [toSearchResults, setToSearchResults] = useState(false);
  const history = useHistory();
  const route = useLocation();

  const [loaded, setLoaded] = useState(false);
  const [locationIndex, setLocationIndex] = useState(0);
  const [mobileSearchDisplay, setMobileSearchDisplay] = useState(false);
  const [coordinates, setCoordinates] = useState({ latitude: 0, longitude: 0 });
  const [location, setLocation] = useState<LandingPage>();
  const [carouselContent, setCarouselContent] = useState<
    LandingPagesCarouselSlide[]
  >();
  const [relatedPages, setRelatedPages] = useState<{ url: string; name: string }[]>([]);

  const [searchContext, setSearchContext] = useContext(SearchContext);
  const [urlContext, setUrlContext] = useContext(UrlContext);
  const [url, setUrl] = useState("");
  const URINoSlash = route.pathname.slice(1);
  const URI = URINoSlash.split('/').slice(-1)[0];
  const category = URINoSlash.split('/').slice(-2)[0];

  const getContent = async () => {
    const contentResponse = await CMSService.getLandingPage(URI, category);
    setLocation(contentResponse.data);
    setLoaded(true);
  };

  const getCarouselContent = async () => {
    const contentResponse = await CMSService.getLandingPagesCarousel(category);
    setCarouselContent(contentResponse.data);
    setLoaded(true);
  };

  // Parse related destination pages from hub page related_urls field
  const getRelatedPages = (landingPage: any) => {
    if (!landingPage.related_urls) return;
    try {
      const urls: string[] = JSON.parse(landingPage.related_urls);
      if (!urls || urls.length === 0) return;
      setRelatedPages(urls.map((u: string) => ({
        url: u,
        name: u.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      })));
    } catch (e) {
      // related_urls not valid JSON — skip
    }
  };

  useEffect(() => {
    setLoaded(false);
    setRelatedPages([]);
    setTimeout(() => {
      setLocationIndex(0);
    }, 200);
    getContent();
    return () => {
      setLocation(undefined);
    };
  }, [route.pathname]);

  useEffect( () => {
    if (urlContext !== "/" ) {
      setUrl(SiteParams.find((obj) => obj.url === urlContext)?.url || url)
		}
	}, [urlContext])

  useEffect(() => {
    getCarouselContent();
    if (location) {
      getRelatedPages(location);
    }
  }, [location])

  useEffect(() => {
    if (loaded && locationIndex === 0) saveLocationIndex(0);
  }, [location, locationIndex, loaded]);

  useEffect(() => {
    const search = () => history.push({ pathname: `${url}/search` });
    if (toSearchResults) search();
    return () => setToSearchResults(false);
  }, [toSearchResults, history]);

  // Geocode a destination name to lat/lng using Nominatim (OpenStreetMap, free)
  const geocodeDestination = async (name: string): Promise<{ lat: number; lng: number } | null> => {
    try {
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(name + ', Australia')}&format=json&limit=1`,
        { headers: { 'Accept': 'application/json' } }
      );
      const data = await resp.json();
      if (data && data[0]) {
        return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
      }
    } catch (e) {}
    return null;
  };

  const searchLocation = async () => {
    if (!location) return;
    if (location.featuredLocations && location.featuredLocations.length > 0) {
      // Linked Locations record — use stored coordinates
      const { latitude, longitude, radius, name } = location.featuredLocations[
        locationIndex
      ].Locations_id;
      setSearchContext({
        ...searchContext,
        coordinates: { lat: latitude, lng: longitude },
        radius: radius,
        destination: name,
        startDate: twoWeeks,
        endDate: twoWeeksTomorrow,
      });
      setToSearchResults(true);
    } else {
      // Programmatic page — geocode the destination name then navigate
      const coords = await geocodeDestination(location.name);
      setSearchContext({
        ...searchContext,
        ...(coords ? { coordinates: coords } : {}),
        radius: (location as any).destinationSearchRadius || 20,
        destination: location.name,
        destinationShortName: location.name,
        startDate: twoWeeks,
        endDate: twoWeeksTomorrow,
      });
      setToSearchResults(true);
    }
  };

  const saveLocationIndex = (index: number) => {
    if (!location) return;
    if (!location.featuredLocations || location.featuredLocations.length === 0) return;
    const latitude = location.featuredLocations[index].Locations_id.latitude;
    const longitude = location.featuredLocations[index].Locations_id.longitude;
    const radius = location.featuredLocations[index].Locations_id.radius
      ? location.featuredLocations[index].Locations_id.radius
      : searchContext.radius;
    setLocationIndex(index);
    setCoordinates({ latitude: latitude, longitude: longitude });
    setSearchContext({
      ...searchContext,
      coordinates: { lat: latitude, lng: longitude },
      radius: radius,
      destination: location.featuredLocations[index].Locations_id.name,
      destinationShortName: location.featuredLocations[index].Locations_id.name,
    });
    console.log(
      `Set location for ${location.featuredLocations[index].Locations_id.name}`
    );
  };

  // Falls back to the page name (e.g. "Gold Coast") when no featuredLocations are linked
  const locationVisibleName = (index: number) => {
    if (!location) return undefined;
    return location.featuredLocations?.[index]?.Locations_id?.name || location.name;
  };

  return (
    <div>
      <Head
        canonical={(() => {
          const suffixes = ['-pet-friendly','-family','-couples','-budget','-self-contained','-last-minute','-summer','-winter'];
          for (const s of suffixes) {
            if (URI.endsWith(s)) return `https://peakah.com/${category}/${URI.slice(0, -s.length)}`;
          }
          return `https://peakah.com${route.pathname}`;
        })()}
        metaData={{
          description: location?.meta_description,
          title: location?.meta_title,
          ogImage: location?.heroImageUrl,
        }}
      />
      {location && (
        <HeaderInner
          heroBg={{
            source: location?.heroImageUrl,
            alt: location?.heroImageAlt,
          }}
          heading={location?.heading}
        />
      )}
      <div className="px-5 sm:px-10 max-w-xxl mx-auto flex items-start">
        <div className="w-full lg:w-6/12">
          <LocationInner
            subHeading={location?.subHeading}
            introduction={location?.introduction}
            loaded={loaded}
            data={location?.featuredLocations}
            locationChange={saveLocationIndex}
          />

          {/* Related Destinations Grid — rendered on hub pages with related_urls */}
          {relatedPages.length > 0 && (
            <div className="mt-8 mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Destinations in this region
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {relatedPages.map((page) => (
                  <Link
                    key={page.url}
                    to={`/${category}/${page.url}`}
                    className="block px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:border-blue-400 hover:text-blue-700 hover:bg-blue-50 transition-colors duration-150"
                  >
                    {page.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="hidden w-full lg:w-5/12 lg:block sticky top-0 ml-auto z-40 lg:px-6 my-10 sm:my-20 sm:pt-5">
          <SearchSidebar
            coordinates={coordinates}
            name={loaded && locationVisibleName(locationIndex)}
            search={searchContext}
          />
        </div>
      </div>
      <div className="pl-5 sm:pl-10 max-w-xxl mx-auto mb-10 xl:pr-10">
        <div>
          {carouselContent && (
            <Carousel
              heading="Get to know your state with a Staycation"
              images={carouselContent}
              exclude={URI}
            />
          )}
        </div>
      </div>
      {mobileSearchDisplay && (
        <MobileOverlay
          darkMode={false}
          label="Close"
          clicked={() => setMobileSearchDisplay(false)}
        >
          <SearchSidebar
            coordinates={coordinates}
            name={loaded && locationVisibleName(locationIndex)}
            search={searchContext}
          />
        </MobileOverlay>
      )}
      <StickySearch
        display={true}
        name={locationVisibleName(locationIndex)}
        click={searchLocation}
      />
      <Footer />
    </div>
  );
};
